import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  VStack,
  HStack,
  Text,
  Progress,
  Badge,
  Avatar,
  AvatarGroup,
  Divider,
  Box,
  Link,
  Input,
  Textarea,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from '@chakra-ui/react';
import { DrawerContainer } from 'src/components/molecules/DrawerContainer';
import { Icon, AvailableIcon } from 'src/components/atoms/Icon';
import { IconButton } from 'src/components/atoms/IconButton';
import { Button } from 'src/components/atoms/Button';
import { GoalDetail, GoalComment } from 'src/features/Goals/types';
import { stripFullModelSuffix } from 'src/utils/model';
import { useCurrency } from 'src/hooks/useCurrency';
import {
  formatMetricValue,
  formatMetricLabel,
  formatGoalDate,
  STATUS_DISPLAY,
  STATUS_COLORS,
} from 'src/features/Goals/utils/formatters';
import { dayjs } from 'src/libs/dayjs';
import {
  useMentionInput,
  MentionUser,
} from 'src/features/Goals/hooks/useMentionInput';
import { MentionDropdown } from 'src/features/Goals/components/atoms/MentionDropdown';

type GoalDetailsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  goal: GoalDetail | null;
  onViewLinkedData: (goal: GoalDetail) => void;
  onAddComment: (
    content: string,
    parentId?: string,
    mentionedUserIds?: string[],
  ) => void;
  onEditComment: (
    commentId: string,
    content: string,
    mentionedUserIds?: string[],
  ) => void;
  onDeleteComment: (commentId: string) => void;
  isLoadingComments?: boolean;
  users?: MentionUser[];
};

// Group comments into threads (top-level with replies)
const groupComments = (comments: GoalComment[]) => {
  const topLevel = comments.filter((c) => c.parentId === null);
  const replies = comments.filter((c) => c.parentId !== null);

  return topLevel.map((comment) => ({
    ...comment,
    replies: replies.filter((r) => r.parentId === comment.id),
  }));
};

export const GoalDetailsDrawer: React.FC<GoalDetailsDrawerProps> = ({
  isOpen,
  onClose,
  goal,
  onViewLinkedData,
  onAddComment,
  onEditComment,
  onDeleteComment,
  isLoadingComments = false,
  users = [],
}) => {
  const { code: currencyCode } = useCurrency();
  const [commentText, setCommentText] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const commentInputRef = useRef<HTMLInputElement>(null);

  const commentMention = useMentionInput({
    users,
    text: commentText,
    onTextChange: setCommentText,
  });

  // Helper to extract mentioned user IDs from content
  const extractMentionedUserIds = useCallback(
    (content: string): string[] => {
      const ids: string[] = [];
      const sortedUsers = [...users].sort(
        (a, b) => b.name.length - a.name.length,
      );

      let remaining = content;
      while (remaining.length > 0) {
        const atIndex = remaining.indexOf('@');
        if (atIndex === -1) break;

        const textAfterAt = remaining.slice(atIndex + 1);
        for (const user of sortedUsers) {
          if (textAfterAt.toLowerCase().startsWith(user.name.toLowerCase())) {
            if (!ids.includes(user.id)) {
              ids.push(user.id);
            }
            remaining = remaining.slice(atIndex + 1 + user.name.length);
            break;
          }
        }
        remaining = remaining.slice(atIndex + 1);
      }

      return ids;
    },
    [users],
  );

  // Group campaigns by category
  const campaignsByCategory = useMemo(() => {
    if (!goal) return {};
    return goal.campaigns.reduce<Record<string, string[]>>((acc, campaign) => {
      const category = campaign.category || 'Uncategorized';
      if (!acc[category]) acc[category] = [];
      acc[category].push(campaign.name);
      return acc;
    }, {});
  }, [goal]);

  // Group comments into threads
  const commentThreads = useMemo(() => {
    if (!goal) return [];
    return groupComments(goal.comments);
  }, [goal]);

  const statusConfig = goal ? STATUS_COLORS[goal.status] : null;

  const handleViewLinkedData = useCallback(() => {
    if (goal) onViewLinkedData(goal);
  }, [goal, onViewLinkedData]);

  const handleSubmitComment = useCallback(() => {
    if (commentText.trim()) {
      const userIds = extractMentionedUserIds(commentText);
      onAddComment(commentText.trim(), undefined, userIds);
      setCommentText('');
    }
  }, [commentText, onAddComment, extractMentionedUserIds]);

  const handleSubmitReply = useCallback(() => {
    if (replyText.trim() && replyingTo) {
      const userIds = extractMentionedUserIds(replyText);
      onAddComment(replyText.trim(), replyingTo, userIds);
      setReplyText('');
      setReplyingTo(null);
    }
  }, [replyText, replyingTo, onAddComment, extractMentionedUserIds]);

  const handleStartEdit = useCallback((comment: GoalComment) => {
    setEditingComment(comment.id);
    setEditText(comment.content);
  }, []);

  const handleSubmitEdit = useCallback(() => {
    if (editText.trim() && editingComment) {
      const userIds = extractMentionedUserIds(editText);
      onEditComment(editingComment, editText.trim(), userIds);
      setEditingComment(null);
      setEditText('');
    }
  }, [editText, editingComment, onEditComment, extractMentionedUserIds]);

  const handleCancelEdit = useCallback(() => {
    setEditingComment(null);
    setEditText('');
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyingTo(null);
    setReplyText('');
  }, []);

  if (!goal || !statusConfig) return null;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContainer>
        <DrawerCloseButton />
        <DrawerHeader pb={4} pt={12}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="28px"
            color="content.title"
            mb={2}>
            {goal.title}
          </Text>

          {/* Progress Display */}
          <HStack justify="space-between" mb={0.5}>
            <Text fontSize="xs" color="content.muted">
              Current / Target
            </Text>
            <Text fontSize="xs" color="content.muted">
              Progress
            </Text>
          </HStack>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="lg" fontWeight="semibold">
              {formatMetricValue(
                goal.currentValue,
                goal.metric,
                goal.kpiUnit,
                currencyCode,
              )}{' '}
              /{' '}
              {formatMetricValue(
                goal.targetValue,
                goal.metric,
                goal.kpiUnit,
                currencyCode,
              )}
            </Text>
            <Text fontSize="lg" color="content.muted">
              {Math.round(goal.progressPercent)}%
            </Text>
          </HStack>
          <Progress
            value={goal.progressPercent}
            size="sm"
            colorScheme="green"
            borderRadius="full"
            mb={3}
          />

          {/* View Linked Data Link */}
          <Box textAlign="right">
            <Link
              color="green.500"
              fontSize="sm"
              fontWeight="medium"
              onClick={handleViewLinkedData}
              display="inline-flex"
              alignItems="center"
              gap={1}>
              <Icon iconName="external-link" boxSize={4} />
              View Linked Data
            </Link>
          </Box>
        </DrawerHeader>

        <DrawerBody p={6}>
          <VStack align="stretch" spacing={4}>
            {/* Model Name */}
            <DetailRow
              icon="graph"
              label="Model"
              value={stripFullModelSuffix(goal.modelName)}
            />

            {/* Goal Period */}
            <DetailRow
              icon="calendar"
              label="Goal Period"
              value={`${formatGoalDate(goal.goalPeriodStart)} - ${formatGoalDate(goal.goalPeriodEnd)}`}
            />

            {/* Status */}
            <HStack justify="space-between">
              <HStack>
                <Icon
                  iconName="circle-checkbox"
                  boxSize={4}
                  color="content.muted"
                />
                <Text fontWeight="medium" color="content.muted">
                  Status
                </Text>
              </HStack>
              <Badge
                bg={statusConfig.bg}
                color={statusConfig.color}
                px={3}
                py={1}
                borderRadius="full">
                {STATUS_DISPLAY[goal.status]}
              </Badge>
            </HStack>

            {/* Mentioned Users */}
            {goal.mentionedUsers.length > 0 && (
              <HStack justify="space-between">
                <HStack>
                  <Icon iconName="users" boxSize={4} color="content.muted" />
                  <Text fontWeight="medium" color="content.muted">
                    Mentioned
                  </Text>
                </HStack>
                <AvatarGroup size="sm" max={5}>
                  {goal.mentionedUsers.map((user) => (
                    <Avatar
                      key={user.id}
                      name={user.name}
                      src={user.avatarUrl}
                    />
                  ))}
                </AvatarGroup>
              </HStack>
            )}

            {/* Campaign Group */}
            {goal.campaignGroup && (
              <Box>
                <Text fontWeight="bold" color="content.title" mb={2}>
                  Campaign Group
                </Text>
                <Text fontSize="sm" color="content.muted">
                  {goal.campaignGroup.name}
                </Text>
              </Box>
            )}

            {/* Campaigns Linked */}
            <Box>
              <Text fontWeight="bold" color="content.title" mb={2}>
                Campaigns linked
              </Text>
              {Object.entries(campaignsByCategory).map(([category, names]) => (
                <Box key={category} mb={2}>
                  <Text fontSize="sm" color="green.500" fontWeight="medium">
                    {category}
                  </Text>
                  <Text fontSize="sm" color="content.muted">
                    {names.join(', ')}
                  </Text>
                </Box>
              ))}
            </Box>

            {/* Metric */}
            <Box>
              <Text fontWeight="bold" color="content.title" mb={2}>
                Metric
              </Text>
              <Text fontSize="sm" color="content.muted">
                {formatMetricLabel(goal.metric)}
              </Text>
            </Box>

            {/* Media Channels */}
            {goal.mediaChannels.length > 0 && (
              <Box>
                <Text fontWeight="bold" color="content.title" mb={2}>
                  Media Channels
                </Text>
                <Text fontSize="sm" color="content.muted">
                  {goal.mediaChannels.join(', ')}
                </Text>
              </Box>
            )}

            {/* Description */}
            {goal.description && (
              <Box>
                <Text fontWeight="bold" color="content.title" mb={2}>
                  Description
                </Text>
                <Text fontSize="sm" color="content.muted">
                  {goal.description}
                </Text>
              </Box>
            )}

            <Divider />

            {/* Comments Section */}
            <Box>
              <Text fontWeight="bold" color="content.title" mb={4}>
                Comments ({goal.commentsCount})
              </Text>

              <VStack align="stretch" spacing={4} mb={4}>
                {commentThreads.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    replies={comment.replies}
                    isEditing={editingComment === comment.id}
                    editText={editText}
                    onEditTextChange={setEditText}
                    onStartEdit={handleStartEdit}
                    onSubmitEdit={handleSubmitEdit}
                    onCancelEdit={handleCancelEdit}
                    onDelete={onDeleteComment}
                    onStartReply={() => setReplyingTo(comment.id)}
                    isReplying={replyingTo === comment.id}
                    replyText={replyText}
                    onReplyTextChange={setReplyText}
                    onSubmitReply={handleSubmitReply}
                    onCancelReply={handleCancelReply}
                    editingCommentId={editingComment}
                    users={users}
                  />
                ))}
              </VStack>

              {/* Add Comment Input with Mentions */}
              <Box position="relative">
                <HStack>
                  <Input
                    ref={commentInputRef}
                    placeholder="Write a comment..."
                    value={commentText}
                    onChange={commentMention.handleChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !commentMention.showMentions) {
                        handleSubmitComment();
                      }
                      if (e.key === 'Escape') {
                        commentMention.closeMentions();
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => commentMention.closeMentions(), 200);
                    }}
                  />
                  <IconButton
                    icon={<Icon iconName="mail-up" boxSize={4} />}
                    aria-label="Send comment"
                    bg="icon.green"
                    color="white"
                    _hover={{ bg: 'green.600' }}
                    onClick={handleSubmitComment}
                    isLoading={isLoadingComments}
                  />
                </HStack>

                {/* Mentions Dropdown */}
                {commentMention.showMentions && (
                  <MentionDropdown
                    users={commentMention.filteredUsers}
                    onSelect={commentMention.handleSelectMention}
                    maxHeight="200px"
                  />
                )}
              </Box>
            </Box>
          </VStack>
        </DrawerBody>
      </DrawerContainer>
    </Drawer>
  );
};

// ============ Sub-components ============

// Helper to render comment content with highlighted mentions
const renderContentWithMentions = (
  content: string,
  users: MentionUser[],
): React.ReactNode => {
  if (!users.length) return content;

  // Sort users by name length (longest first) to match longer names first
  const sortedUsers = [...users].sort((a, b) => b.name.length - a.name.length);

  const parts: React.ReactNode[] = [];
  let remaining = content;
  let keyIndex = 0;

  while (remaining.length > 0) {
    // Find the next @ symbol
    const atIndex = remaining.indexOf('@');

    if (atIndex === -1) {
      // No more @ symbols, add the rest as plain text
      parts.push(remaining);
      break;
    }

    // Add text before the @ as plain text
    if (atIndex > 0) {
      parts.push(remaining.slice(0, atIndex));
    }

    // Check if text after @ matches any user name
    const textAfterAt = remaining.slice(atIndex + 1);
    let matchedUser: MentionUser | null = null;

    for (const user of sortedUsers) {
      if (textAfterAt.toLowerCase().startsWith(user.name.toLowerCase())) {
        matchedUser = user;
        break;
      }
    }

    if (matchedUser) {
      // Add highlighted mention
      parts.push(
        <Text
          as="span"
          key={keyIndex++}
          color="green.500"
          fontWeight="medium"
          cursor="default">
          @{matchedUser.name}
        </Text>,
      );
      remaining = remaining.slice(atIndex + 1 + matchedUser.name.length);
    } else {
      // No match, add @ as plain text and continue
      parts.push('@');
      remaining = remaining.slice(atIndex + 1);
    }
  }

  return parts;
};

const DetailRow: React.FC<{
  icon?: AvailableIcon;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <HStack justify="space-between">
    <HStack>
      {icon && <Icon iconName={icon} boxSize={4} color="content.muted" />}
      <Text fontWeight="medium" color="content.muted">
        {label}
      </Text>
    </HStack>
    <Text color="content.title" textAlign="right">
      {value}
    </Text>
  </HStack>
);

type CommentItemProps = {
  comment: GoalComment;
  replies?: GoalComment[];
  isEditing: boolean;
  editText: string;
  onEditTextChange: (text: string) => void;
  onStartEdit: (comment: GoalComment) => void;
  onSubmitEdit: () => void;
  onCancelEdit: () => void;
  onDelete: (commentId: string) => void;
  onStartReply?: () => void;
  isReplying?: boolean;
  replyText?: string;
  onReplyTextChange?: (text: string) => void;
  onSubmitReply?: () => void;
  onCancelReply?: () => void;
  editingCommentId?: string | null;
  isReply?: boolean;
  users?: MentionUser[];
};

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  replies,
  isEditing,
  editText,
  onEditTextChange,
  onStartEdit,
  onSubmitEdit,
  onCancelEdit,
  onDelete,
  onStartReply,
  isReplying,
  replyText,
  onReplyTextChange,
  onSubmitReply,
  onCancelReply,
  editingCommentId,
  isReply = false,
  users = [],
}) => {
  const formattedDate = dayjs(comment.createdAt).format('MMM D, YYYY h:mm A');
  const wasEdited = comment.createdAt !== comment.updatedAt;

  const replyMention = useMentionInput({
    users,
    text: replyText ?? '',
    onTextChange: (text) => onReplyTextChange?.(text),
  });

  const editMention = useMentionInput({
    users,
    text: editText,
    onTextChange: onEditTextChange,
  });

  return (
    <Box>
      <HStack mb={1} justify="space-between">
        <HStack>
          <Avatar size="sm" name={comment.user.name} />
          <Text fontWeight="semibold" fontSize="sm">
            {comment.user.name}
          </Text>
          <Text fontSize="xs" color="content.muted">
            {formattedDate}
            {wasEdited && ' (edited)'}
          </Text>
        </HStack>

        {/* Edit/Delete menu for own comments */}
        {comment.isOwn && !isEditing && (
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <IconButton
                icon={<Text fontSize="lg">⋮</Text>}
                variant="ghost"
                size="sm"
                aria-label="Comment options"
              />
            </PopoverTrigger>
            <PopoverContent w="auto" minW="100px">
              <PopoverBody p={1}>
                <VStack align="stretch" spacing={0}>
                  <Box
                    as="button"
                    px={3}
                    py={2}
                    textAlign="left"
                    fontSize="sm"
                    _hover={{ bg: 'sidebar.itemHoverBg' }}
                    borderRadius="md"
                    onClick={() => onStartEdit(comment)}>
                    Edit
                  </Box>
                  <Box
                    as="button"
                    px={3}
                    py={2}
                    textAlign="left"
                    fontSize="sm"
                    color="red.500"
                    _hover={{ bg: 'sidebar.itemHoverBg' }}
                    borderRadius="md"
                    onClick={() => onDelete(comment.id)}>
                    Delete
                  </Box>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </HStack>

      {isEditing ? (
        <Box ml={10} position="relative">
          <Textarea
            value={editText}
            onChange={editMention.handleChange}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                editMention.closeMentions();
              }
            }}
            onBlur={() => {
              setTimeout(() => editMention.closeMentions(), 200);
            }}
            size="sm"
            mb={2}
          />

          {/* Edit Mentions Dropdown */}
          {editMention.showMentions && (
            <MentionDropdown
              users={editMention.filteredUsers}
              onSelect={editMention.handleSelectMention}
            />
          )}

          <HStack>
            <Button
              size="sm"
              variant="primary"
              onClick={onSubmitEdit}
              buttonName="save-comment">
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onCancelEdit}
              buttonName="cancel-edit">
              Cancel
            </Button>
          </HStack>
        </Box>
      ) : (
        <Text fontSize="sm" color="content.muted" ml={10}>
          {renderContentWithMentions(comment.content, users)}
        </Text>
      )}

      {/* Reply and Edit buttons */}
      {!isEditing && (
        <HStack ml={10} mt={1} spacing={3}>
          {!isReply && (
            <Text
              as="button"
              fontSize="xs"
              color="content.muted"
              _hover={{ color: 'green.500' }}
              onClick={onStartReply}>
              Reply
            </Text>
          )}
          {comment.isOwn && (
            <Text
              as="button"
              fontSize="xs"
              color="content.muted"
              _hover={{ color: 'green.500' }}
              onClick={() => onStartEdit(comment)}>
              Edit
            </Text>
          )}
        </HStack>
      )}

      {/* Replies */}
      {replies?.map((reply) => (
        <Box
          key={reply.id}
          ml={10}
          mt={3}
          pl={3}
          borderLeft="2px solid"
          borderColor="gray.100">
          <CommentItem
            comment={reply}
            isEditing={editingCommentId === reply.id}
            editText={editText}
            onEditTextChange={onEditTextChange}
            onStartEdit={onStartEdit}
            onSubmitEdit={onSubmitEdit}
            onCancelEdit={onCancelEdit}
            onDelete={onDelete}
            isReply
            editingCommentId={editingCommentId}
            users={users}
          />
        </Box>
      ))}

      {/* Reply input */}
      {isReplying && (
        <Box ml={10} mt={2} position="relative">
          <Textarea
            placeholder="Write a reply..."
            value={replyText}
            onChange={replyMention.handleChange}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                replyMention.closeMentions();
              }
            }}
            onBlur={() => {
              setTimeout(() => replyMention.closeMentions(), 200);
            }}
            size="sm"
            mb={2}
          />

          {/* Reply Mentions Dropdown */}
          {replyMention.showMentions && (
            <MentionDropdown
              users={replyMention.filteredUsers}
              onSelect={replyMention.handleSelectMention}
            />
          )}

          <HStack>
            <Button
              size="sm"
              variant="primary"
              onClick={onSubmitReply}
              buttonName="send-reply">
              Reply
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onCancelReply}
              buttonName="cancel-reply">
              Cancel
            </Button>
          </HStack>
        </Box>
      )}
    </Box>
  );
};
